import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getSpeechRecognitionCtor } from "../utils/saferoute";

/**
 * Web Speech API helper for "voice destination". When the driver taps the
 * mic, we listen for one utterance, strip natural-language preambles
 * ("take me to yaba" → "yaba"), then call `onTranscript(text)` so the
 * caller can drop the text into its input and trigger Advise.
 */
export default function useVoiceDestination(onTranscript) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const onTranscriptRef = useRef(onTranscript);

  // Keep the latest callback without forcing the consumer to memoize it.
  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);

  const start = useCallback(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      toast.error("Voice input not supported on this browser");
      return;
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* idempotent — recognition may already be stopped */ }
      recognitionRef.current = null;
    }
    const rec = new Ctor();
    rec.continuous = false;
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    // en-NG isn't broadly supported; en-US is the safest fallback that
    // recognises Nigerian place names well in practice.
    rec.lang = "en-US";
    rec.onstart = () => setListening(true);
    rec.onerror = (e) => {
      setListening(false);
      const code = e?.error || "speech_error";
      if (code === "not-allowed") {
        toast.error("Microphone permission denied");
      } else if (code === "no-speech") {
        toast.message("Didn't catch that — try again");
      } else {
        toast.error("Voice recognition failed");
      }
    };
    rec.onend = () => { setListening(false); recognitionRef.current = null; };
    rec.onresult = (event) => {
      const transcript = (event.results?.[0]?.[0]?.transcript || "").trim();
      if (!transcript) { toast.message("Didn't catch that — try again"); return; }
      // Strip common natural-language preambles so "take me to yaba" → "yaba".
      const cleaned = transcript
        .replace(/^(take me to|go to|navigate to|drive to|head to|let's go to)\s+/i, "")
        .trim();
      onTranscriptRef.current?.(cleaned);
    };
    try { rec.start(); recognitionRef.current = rec; }
    catch { setListening(false); toast.error("Could not start microphone"); }
  }, []);

  const stop = useCallback(() => {
    if (!recognitionRef.current) return;
    try { recognitionRef.current.stop(); } catch { /* idempotent — recognition may already be stopped */ }
    setListening(false);
  }, []);

  // Tear down on unmount so nothing keeps the mic open.
  useEffect(() => () => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* idempotent — recognition may already be stopped */ }
      recognitionRef.current = null;
    }
  }, []);

  return { listening, start, stop, supported: !!getSpeechRecognitionCtor() };
}
